import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { repositoryAPI } from '../services/api'
import ReportContentModal from '../components/ReportContentModal'
import CommitHistoryList from '../components/CommitHistoryList'
import './Page.css'
import './RepositoryDetail.css'

const COMMITS_PAGE_LIMIT = 500

function RepositoryCommits() {
  const { id: slug } = useParams()
  const { isAuthenticated } = useAuth()

  const [repository, setRepository] = useState(null)
  const [commits, setCommits] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportContext, setReportContext] = useState({ type: 'commit', context: {} })

  useEffect(() => {
    if (!slug) return
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const repo = await repositoryAPI.getBySlug(slug)
        setRepository(repo)
        const res = await repositoryAPI.getCommits(slug, {
          limit: COMMITS_PAGE_LIMIT,
        })
        setCommits(Array.isArray(res.history) ? res.history : (res.commits || []))
      } catch (err) {
        setError(err.message || 'Failed to load commits')
        setCommits([])
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [slug])

  if (error) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <p>{error}</p>
          <Link to={slug ? `/repositories/${slug}` : '/repositories'} className="btn btn-primary" style={{ marginTop: '1rem' }}>
            Back to repository
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <Link to={`/repositories/${slug}`} className="btn btn-secondary" style={{ flexShrink: 0 }}>
            ← Repository
          </Link>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>
              Commit history
              {repository?.name && (
                <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                  · {repository.name}
                </span>
              )}
            </h1>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              Commits, merges, and rollbacks
            </p>
          </div>
        </div>
      </div>

      <div className="dashboard-card">
        {loading ? (
          <div className="empty-state">
            <p>Loading...</p>
          </div>
        ) : commits.length === 0 ? (
          <div className="empty-tab-state">
            <p>No commits yet</p>
            <p className="placeholder-note">Commits, merges, and rollbacks will appear here</p>
            <Link to={`/repositories/${slug}`} className="btn btn-secondary" style={{ marginTop: '1rem' }}>
              Back to repository
            </Link>
          </div>
        ) : (
          <CommitHistoryList
            className="commit-history-list"
            items={commits}
            linkProfiles={isAuthenticated}
            onReport={isAuthenticated ? (item) => {
              setReportContext({
                type: 'commit',
                context: {
                  repositorySlug: slug,
                  repositoryTitle: repository?.title,
                  commitId: item.commit_id || item.id,
                  commitHash: item.commit_hash,
                  commitMessage: item.message,
                },
              })
              setShowReportModal(true)
            } : undefined}
          />
        )}
      </div>

      <ReportContentModal
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        type={reportContext.type}
        context={reportContext.context}
      />
    </div>
  )
}

export default RepositoryCommits
